import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Listing } from '../../types/index';
import { motion, AnimatePresence } from 'framer-motion';

// Create motion components
const MotionDiv = motion.create('div');

// Set your Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface MapProps {
  listings: Listing[];
  selectedListing: Listing | null;
  onListingSelect: (listing: Listing) => void;
  onError?: (error: string) => void;
}

const getMarkerIcon = (type: string) => {
  switch (type) {
    case 'garage-sale':
      return '🏠';
    case 'estate-sale':
      return '🏰';
    case 'curb-alert':
      return '📦';
    default:
      return '📍';
  }
};

const getMarkerColor = (type: string) => {
  switch (type) {
    case 'garage-sale':
      return '#F97316'; // orange-500
    case 'estate-sale':
      return '#7C3AED'; // violet-600
    case 'curb-alert':
      return '#10B981'; // emerald-500
    default:
      return '#3B82F6'; // blue-500
  }
};

const getMarkerAriaLabel = (listing: Listing) => {
  return `${listing.title}, ${listing.type} at ${listing.location?.address || ''}`;
};

export default function Map({ listings, selectedListing, onListingSelect, onError }: MapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const popups = useRef<mapboxgl.Popup[]>([]);

  useEffect(() => {
    if (!mapContainer.current) return;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-85.3088, 35.0456], // Chattanooga coordinates
        zoom: 11
      });

      map.current.on('load', () => {
        // Add markers for each listing
        listings.forEach(listing => {
          if (listing.location?.coordinates) {
            const marker = new mapboxgl.Marker()
              .setLngLat([listing.location.coordinates.lng, listing.location.coordinates.lat])
              .addTo(map.current!);

            marker.getElement().addEventListener('click', () => {
              onListingSelect(listing);
            });

            markers.current.push(marker);
          }
        });
      });

      map.current.on('error', (e) => {
        onError?.(e.error.message);
      });

      return () => {
        map.current?.remove();
        markers.current.forEach(marker => marker.remove());
        markers.current = [];
      };
    } catch (error: any) {
      onError?.(error.message);
    }
  }, []);

  // Update markers when listings change
  useEffect(() => {
    if (!map.current) return;

    // Remove existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers
    listings.forEach(listing => {
      if (listing.location?.coordinates) {
        const marker = new mapboxgl.Marker()
          .setLngLat([listing.location.coordinates.lng, listing.location.coordinates.lat])
          .addTo(map.current!);

        marker.getElement().addEventListener('click', () => {
          onListingSelect(listing);
        });

        markers.current.push(marker);
      }
    });
  }, [listings, onListingSelect]);

  // Center map on selected listing
  useEffect(() => {
    if (!map.current || !selectedListing?.location?.coordinates) return;

    map.current.flyTo({
      center: [selectedListing.location.coordinates.lng, selectedListing.location.coordinates.lat],
      zoom: 14,
      duration: 2000
    });
  }, [selectedListing]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
      <AnimatePresence>
        {selectedListing && (
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{selectedListing.title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{selectedListing.location.address}</p>
          </MotionDiv>
        )}
      </AnimatePresence>
    </div>
  );
}